import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Loader2, Gem, ShieldX, ChevronRight } from "lucide-react";
import { api, assetUrl, errMsg } from "@/lib/api";

export default function CertSearch() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("cert") || "");
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState(null);
  const [err, setErr] = useState("");

  const search = async (value) => {
    const v = value.trim();
    if (!v) return;
    setBusy(true);
    setErr("");
    setHits(null);
    try {
      const { data } = await api.get("/public/lookup", { params: { cert_no: v } });
      if (data.length !== 1) setParams({ cert: v }, { replace: true });
      if (data.length === 1) return nav(`/verify/${data[0].record_id}`, { replace: true });
      setHits(data);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (params.get("cert")) search(params.get("cert"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFBEB] text-stone-900" data-testid="cert-search-page">
      <div className="max-w-xl mx-auto px-5 pt-20 pb-10 space-y-8">
        <header className="text-center rise">
          <div className="w-14 h-14 mx-auto rounded-full border border-amber-400 grid place-items-center text-amber-700"><Gem size={24} /></div>
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-700 mt-5">Jewellery authenticity check</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-2" style={{ fontFamily: "Cinzel, serif" }}>Verify your certificate</h1>
          <p className="text-sm text-stone-600 mt-3">Enter the certificate number printed on your jewellery card.</p>
        </header>

        <form onSubmit={(e) => { e.preventDefault(); search(q); }} className="flex gap-2 rise" style={{ animationDelay: "80ms" }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. JWC-2026-00001" autoFocus style={{ textTransform: q ? "uppercase" : "none" }}
            className="flex-1 h-12 rounded-xl border border-amber-300 bg-white px-4 min-w-0 font-mono text-base tracking-wider outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="cert-search-input" />
          <button disabled={busy || !q.trim()} data-testid="cert-search-button"
            className="h-12 px-4 shrink-0 rounded-xl bg-stone-900 text-amber-100 font-semibold text-sm flex items-center gap-2 hover:bg-stone-800 disabled:opacity-50 transition-colors">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Verify
          </button>
        </form>

        {err && <p className="text-sm text-red-600 text-center" data-testid="cert-search-error">{err}</p>}

        {hits && hits.length === 0 && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center" data-testid="cert-search-not-found">
            <ShieldX size={32} className="mx-auto text-red-600" />
            <p className="font-semibold mt-2 text-red-800">No certificate found</p>
            <p className="text-xs text-red-700/80 mt-1">Check the number and try again, or contact your jeweller.</p>
          </div>
        )}

        {hits && hits.length > 1 && (
          <div className="space-y-2" data-testid="cert-search-results">
            <p className="text-xs text-stone-500 text-center">This number is registered with more than one store. Choose yours:</p>
            {hits.map((h, i) => (
              <Link key={h.record_id} to={`/verify/${h.record_id}`} data-testid={`cert-search-result-${i}`}
                className="flex items-center gap-3 rounded-xl border border-amber-200 bg-white px-4 py-3 hover:border-amber-400 transition-colors">
                {h.logo ? <img src={assetUrl(h.logo)} alt="" className="w-10 h-10 object-contain" /> : <Gem size={20} className="text-amber-600" />}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{h.store_name || "Jeweller"}</p>
                  <p className="text-xs text-stone-500 truncate">{h.cert_no}{h.item ? ` · ${h.item}` : ""}</p>
                </div>
                <ChevronRight size={16} className="text-stone-400" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
